import React,{useState,useEffect,useRef,useCallback} from 'react';
import {useLocation} from 'react-router-dom';
import {motion,AnimatePresence,useReducedMotion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const {FiX,FiCheck,FiArrowRight,FiCalendar,FiLightbulb,FiBarChart2,FiClock,FiSettings,FiTrendingUp,FiRocket,FiSearch}=FiIcons;
const CALCULATOR_BASE_URL='https://app.bpasolutions.au/hidden-cost-calculator';
const CONSULTATION_BASE_URL='https://tidycal.com/anishmmalhotra/initial-20-minutes-discussion';
const CAMPAIGNS=[
 {id: 'c1',weight: 25,headline: 'Do you know what manual work is really costing your business?',body: 'Most businesses underestimate the time lost to repetitive administration, spreadsheets and avoidable rework. Discover your estimated annual hidden cost in around two minutes.',buttonText: 'Calculate My Hidden Cost',icon: FiLightbulb},
 {id: 'c2',weight: 20,headline: 'Find out what manual effort is costing your business.',body: 'Estimate the hidden annual cost of repetitive administration, duplicated data entry and inefficient workflows.',buttonText: 'Show My Cost',icon: FiBarChart2},
 {id: 'c3',weight: 15,headline: 'How much time and money is disappearing into manual work?',body: 'A few simple answers can reveal how much repetitive processes may be costing your business every year.',buttonText: 'Run the Calculation',icon: FiClock},
 {id: 'c4',weight: 10,headline: 'Are manual processes slowing your business down?',body: 'Discover whether repetitive administration may be limiting productivity, capacity and business growth.',buttonText: 'Find Out',icon: FiSettings},
 {id: 'c5',weight: 15,headline: 'Could your business be losing thousands each year to manual work?',body: 'Estimate the impact of manual administration, disconnected spreadsheets and avoidable rework in under two minutes.',buttonText: 'Estimate My Cost',icon: FiTrendingUp},
 {id: 'c6',weight: 10,headline: 'Are you ready for automation?',body: 'Start by understanding where manual effort may be costing your business the most.',buttonText: 'Start My Assessment',icon: FiRocket},
 {id: 'c7',weight: 5,headline: 'What if your admin costs are much higher than you think?',body: 'Use the calculator to estimate the hidden annual cost of repetitive business processes.',buttonText: 'Check My Business',icon: FiSearch}
];
const BENEFITS=['Takes approximately 2 minutes','Provides a personalised estimate','Free to use with no obligation'];
const EYEBROW='BUSINESS EFFICIENCY CHECK';
const TRUST_STATEMENT='Results are based on the information you provide.';
const DISMISS_LABEL='Not now, continue browsing';
const LS_DISMISSED='bpas_manual_cost_popup_dismissed_at';
const LS_CONVERTED='bpas_manual_cost_popup_converted_at';
const SS_SEEN='bpas_manual_cost_popup_seen_session';
const SS_CAMPAIGN='bpas_manual_cost_popup_campaign';
const MOBILE_MIN_MS=50000;
const MOBILE_SCROLL_THRESHOLD=0.5;
const DISMISS_SUPPRESS_MS=7 * 24 * 60 * 60 * 1000;
const CONVERT_SUPPRESS_MS=30 * 24 * 60 * 60 * 1000;
const isBrowser=typeof window !=='undefined';
const isTestMode=()=> {
  if (!isBrowser) return false;
  try {
    return new URLSearchParams(window.location.search).get('popup_test')==='1';
  } catch {
    return false;
  }
};
const safeGetLS=(key)=> {if (!isBrowser) return null;try {return window.localStorage.getItem(key);} catch {return null;}};
const safeSetLS=(key,value)=> {if (!isBrowser || isTestMode()) return;try {window.localStorage.setItem(key,value);} catch {/* ignore */}};
const safeGetSS=(key)=> {if (!isBrowser) return null;try {return window.sessionStorage.getItem(key);} catch {return null;}};
const safeSetSS=(key,value)=> {if (!isBrowser || isTestMode()) return;try {window.sessionStorage.setItem(key,value);} catch {/* ignore */}};
const selectCampaign=()=> {
  const stored=safeGetSS(SS_CAMPAIGN);
  if (stored) {
    const found=CAMPAIGNS.find(c=> c.id===stored);
    if (found) return found;
  }
  const total=CAMPAIGNS.reduce((s,c)=> s + c.weight,0);
  let r=Math.random() * total;
  let chosen=CAMPAIGNS[0];
  for (const c of CAMPAIGNS) {
    r -=c.weight;
    if (r <=0) {chosen=c;break;}
  }
  safeSetSS(SS_CAMPAIGN,chosen.id);
  return chosen;
};
const slugifyPath=(pathname)=> {
  let s=(pathname || '').toLowerCase().replace(/^\/+|\/+$/g,'');
  s=s.replace(/[^a-z0-9]+/g,'_').replace(/^_+|_+$/g,'');
  return s || 'home';
};
const buildCalculatorUrl=(pathname,campaignId)=> {
  try {
    const u=new URL(CALCULATOR_BASE_URL);
    u.searchParams.set('utm_source','bpasolutions_website');
    u.searchParams.set('utm_medium','exit_popup');
    u.searchParams.set('utm_campaign','manual_process_cost_calculator');
    u.searchParams.set('utm_content',slugifyPath(pathname));
    u.searchParams.set('popup_campaign',campaignId);
    u.searchParams.set('cta_type','calculator');
    return u.toString();
  } catch {
    return `${CALCULATOR_BASE_URL}?utm_source=bpasolutions_website&utm_medium=exit_popup&utm_campaign=manual_process_cost_calculator&utm_content=${slugifyPath(pathname)}&popup_campaign=${campaignId}&cta_type=calculator`;
  }
};
const buildConsultationUrl=(pathname,campaignId)=> {
  try {
    const u=new URL(CONSULTATION_BASE_URL);
    u.searchParams.set('utm_source','bpasolutions_website');
    u.searchParams.set('utm_medium','exit_popup');
    u.searchParams.set('utm_campaign','manual_process_cost_calculator');
    u.searchParams.set('utm_content',slugifyPath(pathname));
    u.searchParams.set('popup_campaign',campaignId);
    u.searchParams.set('cta_type','consultation');
    return u.toString();
  } catch {
    return `${CONSULTATION_BASE_URL}?utm_source=bpasolutions_website&utm_medium=exit_popup&utm_campaign=manual_process_cost_calculator&utm_content=${slugifyPath(pathname)}&popup_campaign=${campaignId}&cta_type=consultation`;
  }
};
const isSuppressed=()=> {
  if (isTestMode()) return false;
  const now=Date.now();
  const dismissed=parseInt(safeGetLS(LS_DISMISSED) || '',10);
  if (!isNaN(dismissed) && (now - dismissed) < DISMISS_SUPPRESS_MS) return true;
  const converted=parseInt(safeGetLS(LS_CONVERTED) || '',10);
  if (!isNaN(converted) && (now - converted) < CONVERT_SUPPRESS_MS) return true;
  if (safeGetSS(SS_SEEN)==='1') return true;
  return false;
};
const isExcludedRoute=(pathname)=> {
  if (!pathname) return false;
  const p=pathname.toLowerCase();
  return p==='/resources' || p.startsWith('/resources/') || p==='/contact' || p.startsWith('/contact/');
};
const isExcludedHost=()=> {
  if (!isBrowser) return false;
  try {return window.location.hostname==='app.bpasolutions.au';} catch {return false;}
};
const otherModalActive=()=> {
  if (!isBrowser || !document) return false;
  try {
    const dialogs=document.querySelectorAll('[role="dialog"],[aria-modal="true"]');
    for (const d of dialogs) {
      if (d.id==='bpas-manual-cost-popup') continue;
      const style=window.getComputedStyle(d);
      if (style.display !=='none' && style.visibility !=='hidden' && d.offsetParent !==null) return true;
    }
    const openMenuToggle=document.querySelector('[aria-label="Toggle Menu"][aria-expanded="true"]');
    if (openMenuToggle) return true;
    const mobileNav=document.getElementById('bpas-mobile-navigation');
    if (mobileNav && mobileNav.offsetParent !==null) return true;
  } catch {/* ignore */}
  return false;
};
const trackEvent=(eventName,campaignId,pathname)=> {
  if (!isBrowser || isTestMode()) return;
  try {
    window.dispatchEvent(new CustomEvent('bpas:marketing-event',{detail: {event: eventName,source: 'manual_cost_calculator_popup',page_path: pathname,campaign_id: campaignId}}));
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({event: eventName,source: 'manual_cost_calculator_popup',page_path: pathname,campaign_id: campaignId});
    }
  } catch {/* ignore */}
};
function ManualCostCalculatorPopup() {
  const location=useLocation();
  const [isOpen,setIsOpen]=useState(false);
  const [campaign,setCampaign]=useState(null);
  const [isDesktopDevice,setIsDesktopDevice]=useState(true);
  const [handoff,setHandoff]=useState(null);
  const prevFocusRef=useRef(null);
  const popupRef=useRef(null);
  const ctaRef=useRef(null);
  const closeRef=useRef(null);
  const seenRef=useRef(false);
  const triggeredRef=useRef(false);
  const handoffTimerRef=useRef(null);
  const prefersReducedMotion=useReducedMotion();
  const armDelay=isTestMode() ? 1000 : 5000;
  const markSeen=useCallback(()=> {
    if (seenRef.current) return;
    seenRef.current=true;
    safeSetSS(SS_SEEN,'1');
  },[]);
  const closePopup=useCallback((asDismissal)=> {
    setIsOpen(false);
    if (asDismissal) {
      safeSetLS(LS_DISMISSED,String(Date.now()));
      if (campaign) trackEvent('manual_cost_popup_dismissed',campaign.id,location.pathname);
    }
  },[campaign,location.pathname]);
  const showHandoffNotice=(type)=> {
    setHandoff(type);
  };
  const handleCalculatorClick=useCallback(()=> {
    if (campaign) {
      trackEvent('manual_cost_popup_clicked',campaign.id,location.pathname);
      safeSetLS(LS_CONVERTED,String(Date.now()));
      safeSetSS(SS_SEEN,'1');
      seenRef.current=true;
    }
    setIsOpen(false);
    showHandoffNotice('calculator');
  },[campaign,location.pathname]);
  const handleConsultationClick=useCallback(()=> {
    if (campaign) {
      trackEvent('manual_cost_popup_consultation_clicked',campaign.id,location.pathname);
      safeSetLS(LS_CONVERTED,String(Date.now()));
      safeSetSS(SS_SEEN,'1');
      seenRef.current=true;
    }
    setIsOpen(false);
    showHandoffNotice('consultation');
  },[campaign,location.pathname]);
  // Detect desktop vs mobile device for animation variants
  useEffect(()=> {
    if (!isBrowser) return undefined;

    let mq = null;
    try {
      if (typeof window.matchMedia === 'function') {
        mq = window.matchMedia('(hover: hover) and (pointer: fine)');
      }
    } catch {
      mq = null;
    }

    if (!mq) {
      setIsDesktopDevice(window.innerWidth >= 1024);
      return undefined;
    }

    setIsDesktopDevice(mq.matches);
    const handler=(e)=> setIsDesktopDevice(e.matches);

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change',handler);
      return ()=> mq.removeEventListener('change',handler);
    }

    if (typeof mq.addListener === 'function') {
      mq.addListener(handler);
      return ()=> mq.removeListener(handler);
    }

    return undefined;
  },[]);
  // Main effect: trigger setup per route
  useEffect(()=> {
    if (!isBrowser) return;
    if (isExcludedRoute(location.pathname) || isExcludedHost() || isSuppressed()) return;
    seenRef.current=false;
    triggeredRef.current=false;
    let fallbackTimer=null;
    let exitListener=null;
    let scrollListener=null;
    let startTs=Date.now();
    let scrollReached=false;
    let isDesktop = window.innerWidth >= 1024;
    try {
      if (typeof window.matchMedia === 'function') {
        isDesktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      }
    } catch {
      // Fall back to the viewport-width check when matchMedia is unavailable.
    }
    // Idempotent helper to remove desktop exit listeners
    const removeDesktopExitListeners=()=> {
      if (exitListener) {
        try {document.removeEventListener('mouseout',exitListener);} catch {/* ignore */}
        try {document.documentElement.removeEventListener('mouseleave',exitListener);} catch {/* ignore */}
      }
    };
    const tryShow=()=> {
      if (triggeredRef.current) return;
      if (isSuppressed()) {triggeredRef.current=true;return;}
      if (document.visibilityState !=='visible') return;
      if (otherModalActive()) return;
      if (document.body.style.overflow==='hidden') return;
      triggeredRef.current=true;
      removeDesktopExitListeners();
      const c=selectCampaign();
      setCampaign(c);
      setIsOpen(true);
      markSeen();
      trackEvent('manual_cost_popup_viewed',c.id,location.pathname);
    };
    const onExitIntent=(e)=> {
      if (triggeredRef.current) return;
      if (Date.now() - startTs < armDelay) return;
      if (e.clientY > 10) return;
      if (e.type==='mouseout' && e.relatedTarget !==null) return;
      tryShow();
    };
    const onScroll=()=> {
      if (triggeredRef.current) return;
      let depth=0;
      try {
        const sh=document.documentElement.scrollHeight - window.innerHeight;
        depth=sh > 0 ? Math.min(1,window.scrollY / sh) : 1;
      } catch {depth=0;}
      if (depth >=MOBILE_SCROLL_THRESHOLD) {
        scrollReached=true;
        if (Date.now() - startTs >=MOBILE_MIN_MS) tryShow();
      }
    };
    if (isDesktop) {
      exitListener=(e)=> onExitIntent(e);
      document.addEventListener('mouseout',exitListener);
      document.documentElement.addEventListener('mouseleave',exitListener);
    } else {
      scrollListener=onScroll;
      window.addEventListener('scroll',scrollListener,{passive: true});
      fallbackTimer=setTimeout(()=> {
        if (!triggeredRef.current && scrollReached) tryShow();
      },MOBILE_MIN_MS);
    }
    return ()=> {
      removeDesktopExitListeners();
      if (scrollListener) window.removeEventListener('scroll',scrollListener);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  },[location.pathname,markSeen,armDelay]);
  // Body scroll lock + focus management while open
  useEffect(()=> {
    if (!isBrowser) return;
    if (!isOpen) return;
    prevFocusRef.current=document.activeElement;
    const prevOverflow=document.body.style.overflow;
    document.body.style.overflow='hidden';
    const focusTarget=ctaRef.current || closeRef.current || popupRef.current;
    if (focusTarget) {try {focusTarget.focus();} catch {/* ignore */}}
    const onKey=(e)=> {
      if (e.key==='Escape') {e.preventDefault();closePopup(true);return;}
      if (e.key !=='Tab') return;
      const popup=popupRef.current;
      if (!popup) return;
      const focusables=popup.querySelectorAll('a[href],button:not([disabled]),input,[tabindex]:not([tabindex="-1"])');
      if (focusables.length===0) return;
      const first=focusables[0];
      const last=focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement===first) {e.preventDefault();last.focus();}
      else if (!e.shiftKey && document.activeElement===last) {e.preventDefault();first.focus();}
    };
    document.addEventListener('keydown',onKey);
    return ()=> {
      document.removeEventListener('keydown',onKey);
      document.body.style.overflow=prevOverflow || '';
      if (prevFocusRef.current && typeof prevFocusRef.current.focus==='function') {
        try {prevFocusRef.current.focus();} catch {/* ignore */}
      }
    };
  },[isOpen,closePopup]);
  // Handoff notice timer
  useEffect(()=> {
    if (!handoff) return;
    const startTimer=()=> {
      if (handoffTimerRef.current) clearTimeout(handoffTimerRef.current);
      handoffTimerRef.current=setTimeout(()=> {
        setHandoff(null);
      },6000);
    };
    const stopTimer=()=> {
      if (handoffTimerRef.current) clearTimeout(handoffTimerRef.current);
    };
    const onVisibilityChange=()=> {
      if (document.visibilityState==='visible') {
        startTimer();
      } else {
        stopTimer();
      }
    };
    if (document.visibilityState==='visible') {
      startTimer();
    } else {
      stopTimer();
    }
    document.addEventListener('visibilitychange',onVisibilityChange);
    return ()=> {
      document.removeEventListener('visibilitychange',onVisibilityChange);
      stopTimer();
    };
  },[handoff]);
  if (!campaign) return null;
  const desktopVariants=prefersReducedMotion
    ? {hidden: {opacity: 0},show: {opacity: 1},exit: {opacity: 0}}
    : {hidden: {opacity: 0,y: 16},show: {opacity: 1,y: 0},exit: {opacity: 0,y: 8}};
  const mobileVariants=prefersReducedMotion
    ? {hidden: {opacity: 0},show: {opacity: 1},exit: {opacity: 0}}
    : {hidden: {opacity: 0,y: '100%'},show: {opacity: 1,y: 0},exit: {opacity: 0,y: '100%'}};
  const calcUrl=buildCalculatorUrl(location.pathname,campaign.id);
  const consUrl=buildConsultationUrl(location.pathname,campaign.id);
  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.2}}
            className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={(e)=> {if (e.target===e.currentTarget) closePopup(true);}}
            aria-hidden={false}
          >
            <motion.div
              id="bpas-manual-cost-popup"
              ref={popupRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="bpas-mcp-title"
              aria-describedby="bpas-mcp-desc"
              variants={isDesktopDevice ? desktopVariants : mobileVariants}
              initial="hidden"
              animate="show"
              exit="exit"
              transition={{duration: 0.25,ease: 'easeOut'}}
              className="bg-white w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl shadow-2xl border border-slate-200 overflow-hidden max-h-[80vh] sm:max-h-[90vh] flex flex-col"
            >
              <div className="relative bg-gradient-to-br from-blue-50 to-white px-6 sm:px-10 pt-6 sm:pt-8 pb-4 shrink-0">
                <button
                  ref={closeRef}
                  onClick={()=> closePopup(true)}
                  aria-label="Close popup"
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/80 border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <SafeIcon icon={FiX} className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-blue-600 p-2.5 rounded-xl shrink-0">
                    <SafeIcon icon={campaign.icon} className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-[11px] font-extrabold uppercase tracking-widest text-blue-700">{EYEBROW}</span>
                </div>
                <h2 id="bpas-mcp-title" className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight leading-tight pr-10">
                  {campaign.headline}
                </h2>
              </div>
              <div className="px-6 sm:px-10 py-5 sm:py-6 overflow-y-auto">
                <p id="bpas-mcp-desc" className="text-sm sm:text-base text-slate-600 leading-relaxed mb-5">
                  {campaign.body}
                </p>
                <ul className="space-y-2.5 mb-6">
                  {BENEFITS.map((b,i)=> (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                      <SafeIcon icon={FiCheck} className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-slate-500 italic mb-5">{TRUST_STATEMENT}</p>
                <div className="flex flex-col gap-3">
                  <a
                    ref={ctaRef}
                    href={calcUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleCalculatorClick}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-base font-bold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-lg active:scale-[0.98] min-h-[52px]"
                  >
                    {campaign.buttonText}
                    <SafeIcon icon={FiArrowRight} className="h-4 w-4" />
                  </a>
                  <a
                    href={consUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleConsultationClick}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-base font-bold rounded-xl bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50 transition-all shadow-sm active:scale-[0.98] min-h-[52px]"
                  >
                    <SafeIcon icon={FiCalendar} className="h-4 w-4" />
                    Book a Free 20-Minute Consultation
                  </a>
                  <button
                    onClick={()=> closePopup(true)}
                    className="w-full text-sm font-semibold text-slate-500 hover:text-slate-700 transition-colors py-2 min-h-[44px]"
                  >
                    {DISMISS_LABEL}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {handoff && (
          <motion.div
            role="status"
            aria-live="polite"
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            exit={{opacity: 0,y: 20}}
            className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-80 bg-white rounded-xl shadow-lg border border-slate-200 p-4 z-[80] flex items-start gap-3"
          >
            <div className="bg-green-100 p-2 rounded-lg shrink-0">
              <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-blue-700 mb-1">
                {handoff==='calculator' ? 'CALCULATOR OPENED' : 'BOOKING PAGE OPENED'}
              </p>
              <h3 className="text-sm font-bold text-slate-900 mb-1">
                {handoff==='calculator' ? 'Your calculator is open in a new tab.' : 'Your booking page is open in a new tab.'}
              </h3>
              <p className="text-xs text-slate-600">
                {handoff==='calculator' ? 'Take your time. We will be here when you return.' : 'Choose a time that suits you. We will be here when you return.'}
              </p>
            </div>
            <button
              onClick={()=> setHandoff(null)}
              aria-label="Dismiss confirmation"
              className="p-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <SafeIcon icon={FiX} className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
export default ManualCostCalculatorPopup;